import { Head, useForm } from "@inertiajs/react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { formatDistanceToNow } from "date-fns";
import { PencilIcon, XIcon, MessageCircle } from "lucide-react";
import { useState } from "react";
import { TrackEventCard } from "../components/TrackEventCard";
import type RaceWeekend from "@/types/generated/RaceWeekend";
import { CommentInput } from "../components/CommentInput";
import { CommentsList } from "../components/CommentsList";
import RaceWeekendLikeButton from "../components/RaceWeekendLikeButton";
import AuthLayout from "../layouts/AuthLayout";
import { InertiaPageComponent } from "@/types";
import { PrivacySelect, PrivacyLevel } from "../components/ui/privacy-select";
import { router } from "@inertiajs/react";
import { toast } from "sonner";

interface RaceWeekendShowProps extends RaceWeekend {
  meta?: {
    title: string;
    description: string;
    og_type?: string;
  };
}

const RaceWeekendShow: InertiaPageComponent<RaceWeekendShowProps> = ({
  iracing_session_id,
  earliest_session_time,
  track,
  user,
  sim_car,
  group,
  sessions,
  comments,
  meta,
}) => {
  const [isEditingGroup, setIsEditingGroup] = useState(false);
  const [showCommentInput, setShowCommentInput] = useState(false);
  const [_bulkPrivacy, _setBulkPrivacy] = useState<PrivacyLevel>("friends_only");
  const [_isUpdatingPrivacy, _setIsUpdatingPrivacy] = useState(false);

  const { data, setData, put, processing, reset } = useForm({
    track_event_group: {
      title: group.title || "",
      description: group.description || "",
    },
  });

  // Bulk privacy update - future feature
  // const _handleBulkPrivacyUpdate = async () => {
  //   _setIsUpdatingPrivacy(true);
  //   try {
  //     const updatePromises = sessions.map((session) =>
  //       fetch(`/event/${session.id}`, {
  //         method: "PUT",
  //         headers: {
  //           "Content-Type": "application/json",
  //           "X-CSRF-Token":
  //             document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
  //               ?.content || "",
  //         },
  //         body: JSON.stringify({
  //           track_event: { privacy: _bulkPrivacy },
  //         }),
  //       })
  //     );
  //
  //     await Promise.all(updatePromises);
  //
  //     toast.success("Privacy updated", {
  //       description: `All ${sessions.length} sessions updated to ${_bulkPrivacy}`,
  //     });
  //
  //     // Reload the page to show updated privacy
  //     router.reload({ preserveUrl: true });
  //   } catch (error) {
  //     toast.error("Update failed", {
  //       description: "Failed to update privacy settings",
  //     });
  //   } finally {
  //     _setIsUpdatingPrivacy(false);
  //   }
  // };

  const handleSaveGroup = async (e: React.FormEvent) => {
    e.preventDefault();

    // Update group title/description
    put(`/event_group/${group.id}`, {
      preserveUrl: true,
      onSuccess: async () => {
        // Then update privacy for all sessions
        try {
          const updatePromises = sessions.map((session) =>
            fetch(`/event/${session.id}`, {
              method: "PUT",
              headers: {
                "Content-Type": "application/json",
                "X-CSRF-Token":
                  document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
                    ?.content || "",
              },
              body: JSON.stringify({
                track_event: { privacy: _bulkPrivacy },
              }),
            })
          );

          await Promise.all(updatePromises);

          toast.success("Race weekend updated", {
            description: `Title, description, and privacy for all ${sessions.length} sessions updated`,
          });

          setIsEditingGroup(false);
          router.reload({ preserveUrl: true });
        } catch (error) {
          toast.error("Privacy update failed", {
            description: "Failed to update session privacy settings",
          });
        }
      },
    });
  };

  const handleCancelEdit = () => {
    reset();
    setIsEditingGroup(false);
  };

  const getSessionTypeOrder = (sessionType: string) => {
    switch (sessionType) {
      case "practice":
        return 1;
      case "qualifying":
        return 2;
      case "race":
        return 3;
      default:
        return 4;
    }
  };

  const sortedSessions = [...sessions].sort((a, b) => {
    const orderA = getSessionTypeOrder(a.session_type);
    const orderB = getSessionTypeOrder(b.session_type);
    if (orderA !== orderB) return orderA - orderB;
    return (
      new Date(a.session_started_at || new Date()).getTime() -
      new Date(b.session_started_at || new Date()).getTime()
    );
  });

  // Transform TrackEvent sessions into IndividualEvent format for TrackEventCard
  const individualEvents = sortedSessions.map((session) => ({
    type: "individual" as const,
    ...session,
  }));

  const userName = `${user.first_name} ${user.last_name}`;
  const timestamp = earliest_session_time
    ? formatDistanceToNow(new Date(earliest_session_time), {
        addSuffix: true,
      })
    : null;

  return (
    <>
      <Head title={meta?.title || `${group.title || "Race Weekend"} - ${track?.name || ""}`}>
        {meta?.description && (
          <meta name="description" content={meta.description} />
        )}
        {meta?.og_type && (
          <meta property="og:type" content={meta.og_type} />
        )}
      </Head>
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card className="mb-6">
          <CardHeader className="gap-3">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="text-sm leading-normal">
                  <span className="font-semibold">{userName}</span>
                  {timestamp && (
                    <span className="text-muted-foreground"> · {timestamp}</span>
                  )}
                  <span className="text-muted-foreground">
                    {" "}
                    · Race Weekend ({sessions.length} sessions)
                  </span>
                </div>
                {track && (
                  <div className="text-sm text-muted-foreground mt-0.5">
                    <span className="font-medium">{track.name}</span>
                    {sim_car && (
                      <>
                        {" · "}
                        <span>
                          {sim_car.manufacturer} {sim_car.model}
                        </span>
                      </>
                    )}
                  </div>
                )}
                {isEditingGroup ? (
                  <form onSubmit={handleSaveGroup} className="space-y-3 mt-3">
                    <input
                      type="text"
                      value={data.track_event_group.title}
                      onChange={(e) =>
                        setData("track_event_group", {
                          ...data.track_event_group,
                          title: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 text-lg font-semibold border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                      placeholder="Race weekend title"
                    />
                    <textarea
                      value={data.track_event_group.description}
                      onChange={(e) =>
                        setData("track_event_group", {
                          ...data.track_event_group,
                          description: e.target.value,
                        })
                      }
                      rows={4}
                      className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring font-mono text-sm"
                      placeholder="Description (Markdown supported)"
                    />
                    <div className="pt-2">
                      <PrivacySelect
                        label="Session Privacy"
                        description={`Apply to all ${sessions.length} sessions`}
                        value={_bulkPrivacy}
                        onChange={(value) => _setBulkPrivacy(value)}
                        showDescription={true}
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button type="submit" disabled={processing} size="sm">
                        {processing ? "Saving..." : "Save"}
                      </Button>
                      <Button
                        type="button"
                        onClick={handleCancelEdit}
                        variant="ghost"
                        size="sm"
                      >
                        <XIcon className="w-4 h-4 mr-1" />
                        Cancel
                      </Button>
                    </div>
                  </form>
                ) : (
                  <>
                    <CardTitle className="leading-tight mt-1">
                      {group.title || `${track?.name} - Race Weekend`}
                    </CardTitle>
                    {group.description && (
                      <div
                        className="leading-normal prose prose-sm max-w-none mt-2"
                        dangerouslySetInnerHTML={{
                          __html: group.description_html || "",
                        }}
                      />
                    )}
                  </>
                )}
              </div>
              {!isEditingGroup && (
                <div className="flex items-center gap-2">
                  {group.can_edit && (
                    <Button
                      onClick={() => setIsEditingGroup(true)}
                      variant="ghost"
                      size="sm"
                    >
                      <PencilIcon className="w-4 h-4" />
                    </Button>
                  )}
                  <Button
                    onClick={() => setShowCommentInput(!showCommentInput)}
                    variant="ghost"
                    size="sm"
                    className="flex items-center gap-1.5"
                  >
                    <MessageCircle className="h-5 w-5" />
                    {comments.length > 0 && (
                      <span className="text-sm font-medium">
                        {comments.length}
                      </span>
                    )}
                  </Button>
                  <RaceWeekendLikeButton
                    userId={user.slug}
                    iracingSessionId={iracing_session_id}
                    isLiked={group.is_liked}
                    likesCount={group.likes_count}
                    totalSessions={sessions.length}
                  />
                </div>
              )}
            </div>
          </CardHeader>

          <CardContent className="space-y-4">
            {/* Individual Session Cards */}
            <div className="space-y-4">
              {individualEvents.map((event) => (
                <div key={event.id}>
                  <TrackEventCard trackEvent={event} />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Comments Section */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-base">Weekend Comments</CardTitle>
          </CardHeader>
          <CardContent>
            {showCommentInput && (
              <div className="mb-4 pb-4 border-b">
                <CommentInput
                  commentableType="TrackEventGroup"
                  commentableId={group.id}
                  onCancel={() => setShowCommentInput(false)}
                  autoFocus
                />
              </div>
            )}

            {comments.length > 0 ? (
              <CommentsList comments={comments} maxComments={999} />
            ) : (
              <p className="text-sm text-muted-foreground text-center py-8">
                No comments yet. Be the first to comment on this race weekend!
              </p>
            )}
          </CardContent>
        </Card>
      </main>
    </>
  );
};

RaceWeekendShow.layout = (page) => (
  <AuthLayout>{page}</AuthLayout>
);

export default RaceWeekendShow;
